import React, { useState, useRef } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Squares2X2Icon } from "@heroicons/react/24/outline";
import "react-lazy-load-image-component/src/effects/blur.css";

interface PropertyImage {
  id: number;
  url: string;
  position?: number;
}

interface PropertyImageGalleryProps {
  coverImage: { url: string } | null;
  placeImages: PropertyImage[];
  propertyTitle: string;
  onOpenGallery: () => void;
  defaultImage?: string;
}

const PropertyImageGallery: React.FC<PropertyImageGalleryProps> = ({
  coverImage,
  placeImages = [],
  propertyTitle,
  onOpenGallery,
  defaultImage = "https://via.placeholder.com/800x600?text=No+Image",
}) => {
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const allImages = [
    { id: 0, url: coverImage?.url || defaultImage },
    ...placeImages,
  ];

  const handleMobileScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.offsetWidth;
    const newIndex = Math.round(scrollLeft / containerWidth);
    setCurrentMobileIndex(newIndex);
  };

  return (
    <header className="rounded-xl overflow-hidden mb-8 relative">
      {/* ========== DESKTOP VIEW ========== */}
      <div
        className="hidden md:grid md:grid-cols-4 md:grid-rows-2 gap-2 h-[400px] lg:h-[480px] cursor-pointer"
        onClick={onOpenGallery}
      >
        {/* Main/Cover Image - Left side (2x2) */}
        <div className="col-span-2 row-span-2 relative rounded-l-xl overflow-hidden group">
          <LazyLoadImage
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            src={coverImage?.url || defaultImage}
            alt={`${propertyTitle} - Cover`}
            effect="blur"
            wrapperClassName="w-full h-full"
            placeholderSrc={defaultImage}
          />
          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
        </div>

        {/* Right side images - 2x2 grid */}
        {placeImages.slice(0, 4).map((item, index) => {
          const isLastImage = index === 3;
          const hasMoreImages = placeImages.length > 4;
          const isTopRight = index === 1;
          const isBottomRight = index === 3;

          return (
            <div
              key={item.id}
              className={`relative overflow-hidden group ${
                isTopRight ? "rounded-tr-xl" : ""
              } ${isBottomRight ? "rounded-br-xl" : ""}`}
            >
              <LazyLoadImage
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                src={item.url}
                alt={`${propertyTitle} - Image ${index + 1}`}
                effect="blur"
                wrapperClassName="w-full h-full"
                placeholderSrc={defaultImage}
              />
              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300" />

              {/* Overlay for "+X more photos" on last image */}
              {isLastImage && hasMoreImages && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-white text-center">
                    <span className="text-2xl font-bold">
                      +{placeImages.length - 3}
                    </span>
                    <p className="text-sm font-medium">more photos</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* "Show all photos" button - Desktop - OUTSIDE the grid */}
      <button
        className="hidden md:flex absolute bottom-4 right-4 px-4 py-2.5 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 z-20 items-center gap-2 transition-all duration-200 border border-gray-200 dark:border-gray-600 font-medium text-sm"
        onClick={(e) => {
          e.stopPropagation();
          onOpenGallery();
        }}
      >
        <Squares2X2Icon className="w-5 h-5" />
        <span>Show all photos ({allImages.length})</span>
      </button>

      {/* ========== MOBILE VIEW ========== */}
      <div className="md:hidden relative">
        {/* Scrollable container */}
        <div
          ref={scrollContainerRef}
          onScroll={handleMobileScroll}
          className="flex overflow-x-auto snap-x snap-mandatory -mx-4 px-4 gap-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          style={{ scrollBehavior: "smooth" }}
        >
          {/* All images as full-width cards */}
          {allImages.map((item, index) => (
            <div
              key={item.id || index}
              className="flex-shrink-0 w-full snap-center rounded-xl overflow-hidden"
              onClick={onOpenGallery}
            >
              <LazyLoadImage
                className="w-full h-[280px] sm:h-[320px] object-cover"
                src={item.url}
                alt={`${propertyTitle} - ${index === 0 ? "Cover" : `Image ${index}`}`}
                effect="blur"
                wrapperClassName="w-full h-full"
                placeholderSrc={defaultImage}
              />
            </div>
          ))}
        </div>

        {/* Image counter indicator - Mobile */}
        <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-1.5 shadow-lg">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>
            {currentMobileIndex + 1} / {allImages.length}
          </span>
        </div>

        {/* Scroll dots indicator - Mobile */}
        <div className="flex justify-center gap-1.5 mt-4 px-4">
          {allImages.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (scrollContainerRef.current) {
                  const container = scrollContainerRef.current;
                  const scrollAmount = container.offsetWidth * index;
                  container.scrollTo({ left: scrollAmount, behavior: "smooth" });
                }
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentMobileIndex
                  ? "w-6 bg-gray-900 dark:bg-gray-100"
                  : "w-1.5 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </header>
  );
};

export default PropertyImageGallery;